<template>
  <div class="cr-table-scroll">
    <table class="table table-hover gd-guides-table" tabindex="0" ref="table"
           @keydown="onKeydown">
      <thead>
      <tr>
        <th class="gd-col-check">
          <label class="form-checkbox tooltip tooltip-right" data-tooltip="Selecionar todas"
                 v-if="editableItems.length > 0">
            <input type="checkbox" :checked="allSelected"
                   :indeterminate.prop="partiallySelected" @change="toggleAll">
            <i class="form-icon"></i>
          </label>
        </th>
        <th class="gd-col-date c-hand" :class="{'is-sorted': sort.field === 'startDate'}"
            @click="$emit('sort', 'startDate')">
          Data <fa-icon :icon="['fal', sortIcon('startDate')]" class="gd-sort"/>
        </th>
        <th class="gd-col-type">Tipo</th>
        <th class="gd-col-guide">Guia</th>
        <th class="gd-col-patient c-hand" :class="{'is-sorted': sort.field === 'patient'}"
            @click="$emit('sort', 'patient')">
          Paciente <fa-icon :icon="['fal', sortIcon('patient')]" class="gd-sort"/>
        </th>
        <th class="gd-col-professional hide-md">Profissional</th>
        <th class="gd-col-items hide-md">Itens</th>
        <th class="gd-col-value text-right c-hand"
            :class="{'is-sorted': sort.field === 'insurance'}"
            @click="$emit('sort', 'insurance')">
          Valor <fa-icon :icon="['fal', sortIcon('insurance')]" class="gd-sort"/>
        </th>
        <th class="gd-col-status">Status</th>
        <th class="gd-col-actions"></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, index) in items" :key="item.id"
          :class="{active: selected.includes(item.id)}"
          @click="rowClick(item, $event)">
        <td class="gd-col-check" @click.stop>
          <label class="form-checkbox tooltip tooltip-right" :data-tooltip="selectTooltip(item)">
            <input type="checkbox" :value="item.id" :checked="selected.includes(item.id)"
                   :disabled="!isEditable(item)" @change="toggle(item, index, $event)">
            <i class="form-icon"></i>
          </label>
        </td>
        <td class="gd-col-date gd-num">{{ item.startDate | date }}</td>
        <td class="gd-col-type"><guide-type-badge :type="item.type"/></td>
        <td class="gd-col-guide">
          <span class="text-bold gd-num">{{ item.referral.provider || item.code }}</span>
          <span class="gd-sub" v-if="item.referral.insurance">
            op. {{ item.referral.insurance }}
          </span>
          <span class="gd-sub" v-else-if="item.authorization && item.authorization.password">
            senha {{ item.authorization.password }}
          </span>
        </td>
        <td class="gd-col-patient">
          <span class="text-bold gd-ell">{{ item.patient.name }}</span>
          <span class="gd-sub gd-ell" :class="{'gd-err': validityExpired(item)}"
                v-if="item.insurance.insured.record">
            Cart. {{ item.insurance.insured.record }}
            <template v-if="item.insurance.insured.validity">
              · val. {{ item.insurance.insured.validity | date('MM/YYYY') }}
              <template v-if="validityExpired(item)">(vencida)</template>
            </template>
          </span>
        </td>
        <td class="gd-col-professional hide-md">
          <span class="gd-ell">{{ item.professional.name }}</span>
          <span class="gd-sub gd-ell" v-if="specialtyName(item)">{{ specialtyName(item) }}</span>
        </td>
        <td class="gd-col-items hide-md">
          <span class="gd-num">{{ item.items.length }}</span>
          <span class="gd-sub gd-ell">{{ itemNames(item) }}</span>
        </td>
        <td class="gd-col-value text-right gd-val">{{ item.totals.insurance | currency }}</td>
        <td class="gd-col-status">
          <guide-status-badge :status="item.status"
                              :text="statusText(item)"
                              :batch-number="item.batch ? item.batch.number : null"/>
          <span class="gd-sub" v-if="item.status === 'closed' && item.batch">
            lote nº {{ item.batch.number }}
          </span>
        </td>
        <td class="gd-col-actions" @click.stop>
          <div class="gd-row-actions">
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  :data-tooltip="isEditable(item) ? 'Abrir' : 'Abrir (leitura)'"
                  @click="$emit('open', item)">
            <fa-icon :icon="['fal', isEditable(item) ? 'pencil' : 'eye']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip-left"
                  :class="{loading: printingId === item.id, tooltip: printingId !== item.id}"
                  data-tooltip="Imprimir guia"
                  :disabled="!!printingId" @click="$emit('print', item)">
            <fa-icon :icon="['fal', 'print']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  :data-tooltip="item.batch ? `Ver lote nº ${item.batch.number}` : 'Sem lote'"
                  :disabled="!item.batch || !item.batch.id" @click="$emit('batch', item)">
            <fa-icon :icon="['fal', 'layer-group']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip-left"
                  :class="{loading: deletingId === item.id, tooltip: deletingId !== item.id}"
                  :data-tooltip="deleteTooltip(item)"
                  :disabled="!canDeleteItem(item) || !!deletingId" @click="$emit('remove', item)">
            <fa-icon :icon="['fal', 'times']"/>
          </button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment';
import GuideStatusBadge from './GuideStatusBadge.vue';
import GuideTypeBadge from './GuideTypeBadge.vue';

export default {
  components: {
    GuideStatusBadge,
    GuideTypeBadge,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    printingId: {
      type: String,
      default: null,
    },
    deletingId: {
      type: String,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: Object,
      default: () => ({ field: 'startDate', direction: 'desc' }),
    },
  },
  data() {
    return {
      lastIndex: null,
    };
  },
  computed: {
    editableItems() {
      return this.items.filter(item => this.isEditable(item));
    },
    allSelected() {
      return this.editableItems.length > 0
        && this.editableItems.every(item => this.selected.includes(item.id));
    },
    partiallySelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
  },
  methods: {
    isEditable(item) {
      return item.status === 'open';
    },
    statusText(item) {
      if (item.status === 'open') return 'Registrada';
      if (item.status === 'closed') return 'Encerrada';
      return null;
    },
    specialtyName(item) {
      const { specialty } = item.professional;
      return specialty && specialty.name ? specialty.name : '';
    },
    selectTooltip(item) {
      if (item.status === 'in_batch') return `Já está no lote nº ${item.batch ? item.batch.number : ''}`;
      if (item.status === 'closed') return 'Guia encerrada';
      return 'Selecionar para o lote';
    },
    deleteTooltip(item) {
      if (item.status === 'in_batch') return `No lote nº ${item.batch ? item.batch.number : ''}. Não pode ser excluída`;
      if (item.status === 'closed') return `Guia encerrada com o lote nº ${item.batch ? item.batch.number : ''}`;
      if (!this.canDelete) return 'Sem permissão';
      return 'Excluir';
    },
    canDeleteItem(item) {
      return this.canDelete && item.status === 'open';
    },
    validityExpired(item) {
      const { validity } = item.insurance.insured;
      return !!(validity && moment(validity).isBefore(moment(item.startDate), 'day'));
    },
    itemNames(item) {
      return item.items.map(entry => entry.expense.name).join(', ');
    },
    sortIcon(field) {
      if (this.sort.field !== field) return 'sort';
      return this.sort.direction === 'asc' ? 'sort-up' : 'sort-down';
    },
    emitSelected(ids) {
      this.$emit('update:selected', ids);
    },
    toggle(item, index, event) {
      let ids = [...this.selected];
      const { checked } = event.target;
      const range = event.shiftKey && this.lastIndex !== null
        ? this.items.slice(Math.min(this.lastIndex, index), Math.max(this.lastIndex, index) + 1)
        : [item];
      range.filter(entry => this.isEditable(entry)).forEach((entry) => {
        if (checked && !ids.includes(entry.id)) ids.push(entry.id);
        if (!checked) ids = ids.filter(id => id !== entry.id);
      });
      this.lastIndex = index;
      this.emitSelected(ids);
    },
    toggleAll() {
      if (this.allSelected) {
        this.emitSelected([]);
      } else {
        this.emitSelected(this.editableItems.map(item => item.id));
      }
    },
    rowClick(item) {
      this.$emit('open', item);
    },
    onKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        this.emitSelected(this.editableItems.map(item => item.id));
      }
    },
  },
};
</script>
